import { faHeart } from '@fortawesome/pro-regular-svg-icons'
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons'
import type { IntlShape } from 'react-intl'

import { getWatchlistDateExpires } from '@/helpers/dateUtils'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import {
  trackActionPanelActionClicked,
  trackToggleListingWatched,
  type AnalyticsTracker,
} from '../../helpers/listViewAnalytics'
import type { ListViewSurface } from '../../helpers/listViewSurface'
import { PANEL_ACTION_ORDER } from '../panelActionOrder'
import type { PanelActionDefinition } from '../types'

const ns = 'Listings.ListView.actionPanel.watchlist'

/** 3MP listings are the only ones the watchlist API accepts. */
const isThreeMp = (listing: CombinedListingSearchResult) =>
  listing.siteAbb.toLowerCase() === 'tmp'

/**
 * Add or remove the row's lot from the buyer's watchlist.
 *
 * This runs for each row and we want it to be fast, so it has no hooks.
 */
export const createWatchlistPanelAction = ({
  formatMessage,
  surface,
  tracker,
  isSignedIn,
  isWatched,
  openUnAuthModal,
  toggleWatchlist,
}: {
  formatMessage: IntlShape['formatMessage']
  surface?: ListViewSurface
  tracker: AnalyticsTracker
  isSignedIn: boolean
  isWatched: (listingId: string) => boolean
  openUnAuthModal: (reason: string) => void
  toggleWatchlist: (listing: CombinedListingSearchResult) => void
}): PanelActionDefinition => ({
  id: 'watchlist',
  icon: (listing) => (isWatched(listing.listingId) ? faHeartSolid : faHeart),
  label: formatMessage({ id: `${ns}.label` }),
  description: (listing) =>
    formatMessage({
      id: !listing.biddingEnabled
        ? `${ns}.descriptionBuyNowOnly`
        : `${ns}.description`,
    }),
  order: PANEL_ACTION_ORDER.watchlist,
  isAvailable: isThreeMp,
  onClick: (listing) => {
    // Track, then act — a guest's click is still a click, and is what
    // WatchlistCTA has always reported from the listing detail page.
    trackToggleListingWatched(tracker, {
      isSelected: !isWatched(listing.listingId),
      listingId: listing.listingId,
      storefrontName: listing.storefrontName,
    })
    trackActionPanelActionClicked(tracker, {
      action: 'watchlist',
      listingId: listing.listingId,
      surface,
    })

    // For guests, open the shared panel action auth modal. `toggle` redirects.
    if (!isSignedIn) {
      openUnAuthModal(formatMessage({ id: `${ns}.signInReason` }))
      return
    }

    toggleWatchlist(listing)
  },
})

export const getWatchlistToggleTarget = (
  listing: CombinedListingSearchResult,
  isWatchlisted: boolean
) => ({
  listingId: listing.listingId,
  dateExpires: getWatchlistDateExpires(listing.endTime),
  isWatchlisted,
  currency: listing.currency,
  listingEndTime: listing.endTime.toISOString(),
  storefrontName: listing.storefrontName,
})
